import { getAdminEvaluationPlanCategoryBundle } from '../services/adminEvaluationPlanService';
import { getWorkspaceEvaluationPlanCategoryBundle } from '../services/workspaceEvaluationPlanService';
import type {
  EvaluationPlanDraft,
  EvaluationPlanRowScope,
  EvaluationStandardDraft,
} from '../types/evaluationPlan';
import type { EvaluationPlanCategoryScope } from './evaluationPlanCategorySelection';

export type MergedEvaluationPlanCategoryView = {
  categoryName: string;
  categoryScope: EvaluationPlanCategoryScope;
  standards: EvaluationStandardDraft[];
  plans: EvaluationPlanDraft[];
  planScopeById: Record<string, EvaluationPlanRowScope>;
  standardScopeById: Record<string, EvaluationPlanRowScope>;
  standardsReadOnly: boolean;
};

function tagRows<T extends { id: string }>(
  rows: T[],
  scope: EvaluationPlanRowScope,
  scopeById: Record<string, EvaluationPlanRowScope>
): T[] {
  for (const row of rows) {
    scopeById[row.id] = scope;
  }
  return rows;
}

function mergePlansUnique(
  groups: Array<{ plans: EvaluationPlanDraft[]; scope: EvaluationPlanRowScope }>,
  planScopeById: Record<string, EvaluationPlanRowScope>
): EvaluationPlanDraft[] {
  const byId = new Map<string, EvaluationPlanDraft>();
  for (const group of groups) {
    for (const plan of group.plans) {
      byId.set(plan.id, plan);
      planScopeById[plan.id] = group.scope;
    }
  }
  return Array.from(byId.values()).sort(
    (a, b) => a.sort_order - b.sort_order || a.id.localeCompare(b.id)
  );
}

function mergeStandardsUnique(
  groups: Array<{ standards: EvaluationStandardDraft[]; scope: EvaluationPlanRowScope }>,
  standardScopeById: Record<string, EvaluationPlanRowScope>
): EvaluationStandardDraft[] {
  const byId = new Map<string, EvaluationStandardDraft>();
  for (const group of groups) {
    for (const standard of group.standards) {
      byId.set(standard.id, standard);
      standardScopeById[standard.id] = group.scope;
    }
  }
  return Array.from(byId.values()).sort(
    (a, b) => a.sort_order - b.sort_order || a.id.localeCompare(b.id)
  );
}

/** 카테고리 스코프에 따라 성취기준·평가계획 혼합 뷰를 조립합니다. */
export async function loadMergedEvaluationPlanCategoryView(params: {
  categoryScope: EvaluationPlanCategoryScope;
  categoryName: string;
  workspaceId: string;
  gradeId: string;
  teamWorkspaceId: string | null;
  teamShareGradeId: string | null;
}): Promise<MergedEvaluationPlanCategoryView> {
  const name = params.categoryName.trim();
  const ws = params.workspaceId.trim();
  const gradeId = params.gradeId.trim();
  const teamWs = params.teamWorkspaceId?.trim() || null;
  const teamShareGradeId = params.teamShareGradeId?.trim() || null;
  const planScopeById: Record<string, EvaluationPlanRowScope> = {};
  const standardScopeById: Record<string, EvaluationPlanRowScope> = {};

  if (!name || !ws || !gradeId) {
    return {
      categoryName: name,
      categoryScope: params.categoryScope,
      standards: [],
      plans: [],
      planScopeById,
      standardScopeById,
      standardsReadOnly: true,
    };
  }

  if (params.categoryScope === 'user') {
    const bundle = await getWorkspaceEvaluationPlanCategoryBundle(ws, gradeId, name);
    tagRows(bundle.plans, 'user', planScopeById);
    tagRows(bundle.standards, 'user', standardScopeById);
    return {
      categoryName: name,
      categoryScope: 'user',
      standards: bundle.standards,
      plans: bundle.plans,
      planScopeById,
      standardScopeById,
      standardsReadOnly: false,
    };
  }

  if (params.categoryScope === 'admin') {
    const adminBundle = await getAdminEvaluationPlanCategoryBundle(name);
    let teamBundle = { standards: [] as EvaluationStandardDraft[], plans: [] as EvaluationPlanDraft[] };
    if (teamWs && teamWs !== ws && teamShareGradeId) {
      try {
        teamBundle = await getWorkspaceEvaluationPlanCategoryBundle(teamWs, teamShareGradeId, name);
      } catch {
        teamBundle = { standards: [], plans: [] };
      }
    }
    let userBundle = { standards: [] as EvaluationStandardDraft[], plans: [] as EvaluationPlanDraft[] };
    try {
      userBundle = await getWorkspaceEvaluationPlanCategoryBundle(ws, gradeId, name);
    } catch {
      userBundle = { standards: [], plans: [] };
    }
    const standards = mergeStandardsUnique(
      [
        { standards: adminBundle.standards, scope: 'admin' },
        { standards: teamBundle.standards, scope: 'team' },
        { standards: userBundle.standards, scope: 'user' },
      ],
      standardScopeById
    );
    const plans = mergePlansUnique(
      [
        { plans: adminBundle.plans, scope: 'admin' },
        { plans: teamBundle.plans, scope: 'team' },
        { plans: userBundle.plans, scope: 'user' },
      ],
      planScopeById
    );
    return {
      categoryName: name,
      categoryScope: 'admin',
      standards,
      plans,
      planScopeById,
      standardScopeById,
      standardsReadOnly: false,
    };
  }

  // team category scope
  const standardsSourceWs = teamWs ?? ws;
  const standardsSourceGradeId = teamWs && teamWs !== ws ? teamShareGradeId : gradeId;
  if (!standardsSourceGradeId) {
    return {
      categoryName: name,
      categoryScope: 'team',
      standards: [],
      plans: [],
      planScopeById,
      standardScopeById,
      standardsReadOnly: true,
    };
  }
  const teamBundle = await getWorkspaceEvaluationPlanCategoryBundle(
    standardsSourceWs,
    standardsSourceGradeId,
    name
  );
  let userBundle = { standards: [] as EvaluationStandardDraft[], plans: [] as EvaluationPlanDraft[] };
  if (teamWs && teamWs !== ws) {
    try {
      userBundle = await getWorkspaceEvaluationPlanCategoryBundle(ws, gradeId, name);
    } catch {
      userBundle = { standards: [], plans: [] };
    }
  }
  const standards = mergeStandardsUnique(
    [
      { standards: teamBundle.standards, scope: 'team' },
      { standards: userBundle.standards, scope: 'user' },
    ],
    standardScopeById
  );
  const plans = mergePlansUnique(
    [
      { plans: teamBundle.plans, scope: 'team' },
      { plans: userBundle.plans, scope: 'user' },
    ],
    planScopeById
  );
  return {
    categoryName: name,
    categoryScope: 'team',
    standards,
    plans,
    planScopeById,
    standardScopeById,
    standardsReadOnly: false,
  };
}

export function getEditablePlanIds(
  planScopeById: Record<string, EvaluationPlanRowScope>
): Set<string> {
  const ids = new Set<string>();
  for (const [id, scope] of Object.entries(planScopeById)) {
    if (scope === 'user') ids.add(id);
  }
  return ids;
}

export function getEditableStandardIds(
  standardScopeById: Record<string, EvaluationPlanRowScope>
): Set<string> {
  const ids = new Set<string>();
  for (const [id, scope] of Object.entries(standardScopeById)) {
    if (scope === 'user') ids.add(id);
  }
  return ids;
}

export const EVALUATION_PLAN_ROW_SCOPE_LABELS: Record<EvaluationPlanRowScope, string> = {
  admin: '기본',
  team: '팀장',
  user: '내 평가계획',
};
